import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { DateTime } from "luxon";
import { Controller, useForm } from "react-hook-form";
import type { DateValueType } from "react-tailwindcss-datepicker";
import Autocomplete from "@/components/Autocomplete";
import Button from "@/components/Button";
import DateSelect from "@/components/DateSelect";
import Dialog from "@/components/Dialog";
import TextField from "@/components/TextField";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { GET_MANAGEABLE_SECTIONS } from "@/pages/events/queries";
import useIsSectionScopedForEvents from "@/pages/events/useIsSectionScopedForEvents";
import {
  ACHIEVEMENTS_FOR_MEMBER_QUERY,
  ACHIEVEMENT_TYPES_QUERY,
  findAchievementTypeForMember,
  type MembershipAchievementCategoryEnum,
  type MembershipAchievementForMember,
  type MembershipAchievementType
} from "@/pages/people/[pgaId]/queries";
import {
  UPSERT_ACHIEVEMENT_ATTRIBUTE_MUTATION,
  UPSERT_ACHIEVEMENT_CODE_MUTATION,
  UPSERT_NATIVE_ACHIEVEMENT_MUTATION,
  type UpsertAchievementAttributeMutationVariables,
  type UpsertAchievementCodeMutationVariables,
  type UpsertNativeAchievementMutationVariables
} from "@/pages/people/[pgaId]/mutations";

type AchievementTypeOption = {
  achievementTypeId: string;
  ccmsAttributeType: string | null;
  ccmsCodeType: string | null;
  label: string;
  native: boolean;
  value: string;
};

interface EditAchievementModalProps {
  achievement: MembershipAchievementForMember | null;
  category: MembershipAchievementCategoryEnum;
  mode: "add" | "edit";
  onClose(): void;
  open: boolean;
  pgaId: string;
}

export function buildAchievementTypeOptions(
  achievementTypes: MembershipAchievementType[] = []
): AchievementTypeOption[] {
  return achievementTypes.flatMap<AchievementTypeOption>((achievementType) => {
    if (achievementType.native) {
      return [
        {
          achievementTypeId: achievementType.id,
          ccmsAttributeType: null,
          ccmsCodeType: null,
          label: achievementType.title,
          native: true,
          value: achievementType.id
        }
      ];
    }

    const ccmsCodeType = achievementType.ccmsCodeType?.trim();
    if (ccmsCodeType) {
      return achievementType.codes.flatMap((code) => {
        const value = code.code?.trim();
        if (!value) return [];

        return [
          {
            achievementTypeId: achievementType.id,
            ccmsAttributeType: null,
            ccmsCodeType,
            label: code.title || achievementType.title,
            native: false,
            value
          }
        ];
      });
    }

    const ccmsAttributeType = achievementType.ccmsAttributeType?.trim();
    const attributeCode = achievementType.codes.find((code) => code.code?.trim())?.code?.trim();
    if (!ccmsAttributeType || !attributeCode) return [];

    return [
      {
        achievementTypeId: achievementType.id,
        ccmsAttributeType,
        ccmsCodeType: null,
        label: achievementType.title,
        native: false,
        value: attributeCode
      }
    ];
  });
}

interface FormValues {
  achievementTypeId: string;
  awardRemarks: string;
  ccmsAttributeType: string;
  ccmsCodeType: string;
  code: string;
  expiresOn: string;
  recognizedAt: string;
  recognitionPeriod: string;
  sectionLegacyId: string;
  title: string;
}

type SectionOptionSource = {
  legacyId?: string | null;
  name: string;
};

type ManageableSectionsQueryData = {
  manageableSections: {
    nodes: SectionOptionSource[];
  };
};

const emptyFormValues: FormValues = {
  achievementTypeId: "",
  awardRemarks: "",
  ccmsAttributeType: "",
  ccmsCodeType: "",
  code: "",
  expiresOn: "",
  recognizedAt: "",
  recognitionPeriod: "",
  sectionLegacyId: "",
  title: ""
};

function findSectionLegacyIdByName(sections: SectionOptionSource[] = [], sectionName: string | null | undefined) {
  if (!sectionName?.trim()) return "";

  const normalizedName = sectionName.trim().toLowerCase();

  return sections.find((section) => section.name.trim().toLowerCase() === normalizedName)?.legacyId ?? "";
}

function resolveSectionLegacyId(
  sections: SectionOptionSource[] = [],
  recognizingEntity: { legacyId?: string | null; name?: string | null } | null | undefined
) {
  if (!recognizingEntity) return "";
  if (recognizingEntity.legacyId) return recognizingEntity.legacyId;
  return findSectionLegacyIdByName(sections, recognizingEntity.name);
}

function defaultExpiresOnFromRecognition(recognizedAt: string, defaultExpirationDays?: number | null): string {
  if (!recognizedAt || !defaultExpirationDays) return "";
  return DateTime.fromISO(recognizedAt).plus({ days: defaultExpirationDays }).toISODate() ?? "";
}

function buildSectionOptions(sections: SectionOptionSource[] = []) {
  return [...sections]
    .filter((section) => section.legacyId)
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((section) => ({
      label: section.name,
      value: section.legacyId!
    }));
}

function sectionsForOptions(
  manageableSections: SectionOptionSource[] = [],
  currentEntity?: { legacyId?: string | null; name?: string | null } | null
) {
  if (!currentEntity?.legacyId || !currentEntity.name) return manageableSections;
  if (manageableSections.some((section) => section.legacyId === currentEntity.legacyId)) {
    return manageableSections;
  }

  return [...manageableSections, { legacyId: currentEntity.legacyId, name: currentEntity.name }];
}

function buildUpsertAchievementCodeInput(
  pgaId: string,
  values: FormValues
): UpsertAchievementCodeMutationVariables["input"] {
  const trimmedYear = values.recognitionPeriod.trim();
  const trimmedRemarks = values.awardRemarks.trim();

  return {
    pgaId,
    code: values.code,
    codeType: values.ccmsCodeType,
    asofDate: values.recognizedAt,
    awardYear: trimmedYear || null,
    awardRemarks: trimmedRemarks || null,
    referenceId: values.sectionLegacyId || null
  };
}

function buildUpsertAchievementAttributeInput(
  pgaId: string,
  values: FormValues
): UpsertAchievementAttributeMutationVariables["input"] {
  const trimmedTitle = values.title.trim();
  const trimmedYear = values.recognitionPeriod.trim();
  const parsedYear = trimmedYear ? Number(trimmedYear) : null;

  return {
    pgaId,
    attributeCode: values.code,
    attributeType: values.ccmsAttributeType,
    cvar: trimmedTitle || null,
    dvar: values.recognizedAt || null,
    nvar: parsedYear
  };
}

function buildUpsertNativeAchievementInput(
  pgaId: string,
  values: FormValues,
  achievementId?: string | null
): UpsertNativeAchievementMutationVariables["input"] {
  const trimmedTitle = values.title.trim();
  const trimmedRecognitionPeriod = values.recognitionPeriod.trim();
  const parsedRecognitionYear = trimmedRecognitionPeriod ? Number(trimmedRecognitionPeriod) : null;
  const trimmedRemarks = values.awardRemarks.trim();

  return {
    pgaId,
    achievementTypeId: values.achievementTypeId,
    ...(achievementId ? { id: achievementId } : {}),
    title: trimmedTitle || null,
    recognizedAt: values.recognizedAt,
    expiresOn: values.expiresOn || null,
    recognitionYear:
      parsedRecognitionYear != null && !Number.isNaN(parsedRecognitionYear) ? parsedRecognitionYear : null,
    remarks: trimmedRemarks || null
  };
}

function getCategoryLabel(category: MembershipAchievementCategoryEnum) {
  if (category === "AWARD") return "Award";
  if (category === "CERTIFICATION") return "Certification";
  return "Achievement";
}

export default function EditAchievementModal({
  achievement,
  category,
  mode,
  onClose,
  open,
  pgaId
}: EditAchievementModalProps) {
  const isAddMode = mode === "add";
  const isSectionScoped = useIsSectionScopedForEvents();
  const categoryLabel = getCategoryLabel(category);
  const [achievementTypeQuery, setAchievementTypeQuery] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [sectionQuery, setSectionQuery] = useState("");
  const [debouncedSectionQuery, setDebouncedSectionQuery] = useState("");

  const trimmedSearch = debouncedSearchTerm.trim();
  const trimmedSectionSearch = debouncedSectionQuery.trim();
  const { data: achievementTypesData, loading: achievementTypesLoading } = useQuery(ACHIEVEMENT_TYPES_QUERY, {
    variables: {
      category,
      ...(isAddMode && isSectionScoped ? { canSectionsManage: true } : {}),
      ...(trimmedSearch ? { search: trimmedSearch } : {})
    },
    fetchPolicy: "cache-first",
    skip: !open
  });

  const achievementTypeOptions = useMemo(
    () => buildAchievementTypeOptions(achievementTypesData?.achievementTypes),
    [achievementTypesData?.achievementTypes]
  );

  const [upsertAchievementCode, { loading: savingCode }] = useMutation(UPSERT_ACHIEVEMENT_CODE_MUTATION, {
    refetchQueries: [{ query: ACHIEVEMENTS_FOR_MEMBER_QUERY, variables: { pgaId, achievement: category } }],
    awaitRefetchQueries: true
  });

  const [upsertAchievementAttribute, { loading: savingAttribute }] = useMutation(
    UPSERT_ACHIEVEMENT_ATTRIBUTE_MUTATION,
    {
      refetchQueries: [{ query: ACHIEVEMENTS_FOR_MEMBER_QUERY, variables: { pgaId, achievement: category } }],
      awaitRefetchQueries: true
    }
  );

  const [upsertNativeAchievement, { loading: savingNative }] = useMutation(UPSERT_NATIVE_ACHIEVEMENT_MUTATION, {
    refetchQueries: [{ query: ACHIEVEMENTS_FOR_MEMBER_QUERY, variables: { pgaId, achievement: category } }],
    awaitRefetchQueries: true
  });

  const { control, handleSubmit, reset, getValues, setValue, watch } = useForm<FormValues>({
    defaultValues: emptyFormValues
  });

  const selectedCode = watch("code");
  const ccmsCodeType = watch("ccmsCodeType");
  const ccmsAttributeType = watch("ccmsAttributeType");
  const achievementTypeId = watch("achievementTypeId");
  const recognizedAt = watch("recognizedAt");
  const sectionLegacyId = watch("sectionLegacyId");
  const achievementKind = ccmsCodeType ? "code" : ccmsAttributeType ? "attribute" : achievementTypeId ? "native" : null;
  const selectedAchievementType = achievementTypesData?.achievementTypes?.find(
    (achievementType) => achievementType.id === achievementTypeId
  );
  const expiresOnRequired = Boolean(selectedAchievementType?.defaultExpirationDays);

  const { data: sectionsData, loading: sectionsLoading } = useQuery<ManageableSectionsQueryData>(
    GET_MANAGEABLE_SECTIONS,
    {
      fetchPolicy: "cache-first",
      skip: !open || achievementKind !== "code",
      variables: { input: { search: trimmedSectionSearch || null } }
    }
  );

  const manageableSections = sectionsData?.manageableSections?.nodes;
  const sectionOptions = buildSectionOptions(
    sectionsForOptions(manageableSections, isAddMode ? null : achievement?.recognizingEntity)
  );
  const saving = savingCode || savingAttribute || savingNative;
  const disableRecognizedAt = !isAddMode && achievementKind === "code";

  useEffect(() => {
    const handler = setTimeout(() => {
      if (selectedCode) {
        setDebouncedSearchTerm("");
        return;
      }
      setDebouncedSearchTerm(achievementTypeQuery);
    }, 300);

    return () => clearTimeout(handler);
  }, [achievementTypeQuery, selectedCode]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSectionQuery(sectionLegacyId ? "" : sectionQuery);
    }, 300);

    return () => clearTimeout(handler);
  }, [sectionLegacyId, sectionQuery]);

  useEffect(() => {
    if (!open || !isAddMode) {
      return;
    }

    reset(emptyFormValues);
    setAchievementTypeQuery("");
    setDebouncedSearchTerm("");
    setSectionQuery("");
    setDebouncedSectionQuery("");
  }, [isAddMode, open, reset]);

  useEffect(() => {
    if (!open || !isAddMode) {
      return;
    }

    setValue(
      "expiresOn",
      defaultExpiresOnFromRecognition(recognizedAt, selectedAchievementType?.defaultExpirationDays)
    );
  }, [isAddMode, open, recognizedAt, selectedAchievementType?.defaultExpirationDays, setValue]);

  useEffect(() => {
    if (
      !open ||
      achievementKind !== "code" ||
      sectionsLoading ||
      trimmedSectionSearch ||
      sectionOptions.length !== 1 ||
      sectionLegacyId
    ) {
      return;
    }

    const [onlyOption] = sectionOptions;
    setValue("sectionLegacyId", onlyOption.value);
    setSectionQuery(onlyOption.label);
  }, [achievementKind, open, sectionLegacyId, sectionOptions, sectionsLoading, setValue, trimmedSectionSearch]);

  useEffect(() => {
    if (!open || isAddMode || !achievement) {
      return;
    }

    const matchingAchievementType = findAchievementTypeForMember(
      achievementTypesData?.achievementTypes ?? [],
      achievement
    );
    const codeType = matchingAchievementType?.ccmsCodeType?.trim() ?? "";
    const attributeType = matchingAchievementType?.ccmsAttributeType?.trim() ?? "";
    const code = achievement.code.code?.trim() ?? "";
    const typeLabel = codeType
      ? achievement.code.title || achievement.title || matchingAchievementType?.title || ""
      : matchingAchievementType?.title || achievement.title || "";

    reset({
      achievementTypeId: matchingAchievementType?.id ?? "",
      code,
      ccmsCodeType: codeType,
      ccmsAttributeType: codeType ? "" : attributeType,
      title: achievement.title ?? (codeType ? achievement.code.title : ""),
      awardRemarks: achievement.remarks ?? "",
      recognizedAt: achievement.recognizedAt || achievement.createdDate || "",
      expiresOn: achievement.expiresOn || "",
      recognitionPeriod: achievement.recognitionYear ?? "",
      sectionLegacyId: resolveSectionLegacyId(manageableSections, achievement.recognizingEntity)
    });
    setAchievementTypeQuery(typeLabel);
    setDebouncedSearchTerm("");
    setSectionQuery(achievement.recognizingEntity?.name ?? "");
  }, [achievement, achievementTypesData?.achievementTypes, isAddMode, manageableSections, open, reset]);

  const handleClose = () => {
    reset(emptyFormValues);
    setAchievementTypeQuery("");
    setDebouncedSearchTerm("");
    setSectionQuery("");
    setDebouncedSectionQuery("");
    onClose();
  };

  const onSubmit = async (values: FormValues) => {
    const isNativeType =
      Boolean(values.achievementTypeId.trim()) && !values.ccmsCodeType.trim() && !values.ccmsAttributeType.trim();

    if (!values.code.trim() || (!values.ccmsCodeType.trim() && !values.ccmsAttributeType.trim() && !isNativeType)) {
      showErrorSnackbar(`${categoryLabel} type is required.`);
      return;
    }

    if (!values.recognizedAt) {
      showErrorSnackbar("Recognized At is required.");
      return;
    }

    try {
      if (isNativeType) {
        const result = await upsertNativeAchievement({
          variables: {
            input: buildUpsertNativeAchievementInput(pgaId, values, isAddMode ? null : achievement?.id)
          }
        });

        if (!result.data?.upsertNativeAchievement.success) {
          showErrorSnackbar(
            result.data?.upsertNativeAchievement.message ||
              `Unable to ${isAddMode ? "add" : "update"} ${categoryLabel.toLowerCase()}.`
          );
          return;
        }
      } else if (values.ccmsCodeType) {
        const result = await upsertAchievementCode({
          variables: {
            input: buildUpsertAchievementCodeInput(pgaId, values)
          }
        });

        if (!result.data?.upsertAchievementCode.success) {
          showErrorSnackbar(
            result.data?.upsertAchievementCode.message ||
              `Unable to ${isAddMode ? "add" : "update"} ${categoryLabel.toLowerCase()}.`
          );
          return;
        }
      } else {
        const result = await upsertAchievementAttribute({
          variables: {
            input: buildUpsertAchievementAttributeInput(pgaId, values)
          }
        });

        if (!result.data?.upsertAchievementAttribute.success) {
          showErrorSnackbar(
            result.data?.upsertAchievementAttribute.message ||
              `Unable to ${isAddMode ? "add" : "update"} ${categoryLabel.toLowerCase()}.`
          );
          return;
        }
      }

      showSnackbar(`${categoryLabel} ${isAddMode ? "added" : "updated"}`);
      handleClose();
    } catch {
      showErrorSnackbar(`Unable to ${isAddMode ? "add" : "update"} ${categoryLabel.toLowerCase()}.`);
    }
  };

  if (!open || (!isAddMode && !achievement)) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClosed={handleClose}
      headline={`${isAddMode ? "Add" : "Edit"} ${categoryLabel}`}
      className="w-[640px]"
      hasFormElements
      displayOverflow
      actions={
        <>
          <Button variant="text" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            disabled={saving || achievementTypesLoading || (achievementKind === "code" && sectionsLoading)}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <input type="hidden" name="id" value={achievement?.id} />
        <Controller
          name="code"
          control={control}
          rules={{ required: `${categoryLabel} type is required` }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Autocomplete<AchievementTypeOption>
              variant="searchableDropdown"
              label={category === "AWARD" ? "Award Type" : "Achievement Type"}
              data={achievementTypeOptions}
              query={achievementTypeQuery}
              loadingResults={achievementTypesLoading}
              required
              disabled={!isAddMode}
              error={!!error}
              errorText={error?.message}
              placeholder={`Search ${categoryLabel.toLowerCase()} types`}
              data-testid={`${categoryLabel.toLowerCase()}-type`}
              leadingIcon="search"
              constrainMenuWidth
              menuClassName="max-h-[300px]"
              renderItem={(option) => (
                <div className="flex flex-col">
                  <div className="text-body-large">{option.label}</div>
                  <div className="text-label-medium text-sys-on-surface-variant">
                    {option.native ? "Native" : option.value}
                  </div>
                </div>
              )}
              onChangeText={(text) => {
                setAchievementTypeQuery(text);
                if (value) {
                  onChange("");
                  setValue("achievementTypeId", "", { shouldDirty: true });
                  setValue("ccmsCodeType", "", { shouldDirty: true });
                  setValue("ccmsAttributeType", "", { shouldDirty: true });
                }
              }}
              reset={() => {
                setAchievementTypeQuery("");
                setDebouncedSearchTerm("");
                onChange("");
                setValue("achievementTypeId", "", { shouldDirty: true });
                setValue("ccmsCodeType", "", { shouldDirty: true });
                setValue("ccmsAttributeType", "", { shouldDirty: true });
              }}
              onSelect={(option) => {
                const previousCode = getValues("code");
                const previousOption = achievementTypeOptions.find(
                  (achievementTypeOption) => achievementTypeOption.value === previousCode
                );
                const currentTitle = getValues("title");

                onChange(option.value);
                setValue("achievementTypeId", option.achievementTypeId, { shouldDirty: true });
                setValue("ccmsCodeType", option.ccmsCodeType ?? "", { shouldDirty: true });
                setValue("ccmsAttributeType", option.ccmsAttributeType ?? "", { shouldDirty: true });
                setAchievementTypeQuery(option.label);
                setDebouncedSearchTerm("");
                if (!currentTitle.trim() || currentTitle === previousOption?.label) {
                  setValue("title", option.label, { shouldDirty: true, shouldValidate: true });
                }
              }}
            />
          )}
        />

        {(achievementKind === "attribute" || achievementKind === "native") && (
          <Controller
            name="title"
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextField
                label="Title"
                value={value}
                onChangeText={onChange}
                className="w-full"
                data-testid={`edit-${categoryLabel.toLowerCase()}-title`}
              />
            )}
          />
        )}

        <Controller
          name="recognizedAt"
          control={control}
          rules={{ required: "Recognized At is required" }}
          render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
            <DateSelect
              label="Recognized At *"
              value={{ startDate: value || null, endDate: value || null }}
              onChange={(dateValue: DateValueType | string) => {
                if (typeof dateValue === "string") return;
                onChange(dateValue?.startDate ?? "");
              }}
              onBlur={onBlur}
              useSingleDate
              useMultiCalendar={false}
              useModal
              disabled={disableRecognizedAt}
              error={!!error}
              errorMessage={error?.message}
            />
          )}
        />
        {achievementKind === "native" && (
          <Controller
            name="expiresOn"
            control={control}
            rules={{ required: expiresOnRequired ? "Expires On is required" : false }}
            render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
              <DateSelect
                label={expiresOnRequired ? "Expires On *" : "Expires On"}
                value={{ startDate: value || null, endDate: value || null }}
                onChange={(dateValue: DateValueType | string) => {
                  if (typeof dateValue === "string") return;
                  onChange(dateValue?.startDate ?? "");
                }}
                onBlur={onBlur}
                useSingleDate
                useMultiCalendar={false}
                useModal
                error={!!error}
                errorMessage={error?.message}
              />
            )}
          />
        )}
        <Controller
          name="recognitionPeriod"
          control={control}
          render={({ field: { onChange, value } }) => (
            <TextField
              label="Recognition Period"
              type="number"
              min={1900}
              step={1}
              placeholder="YYYY"
              value={value}
              onChangeText={onChange}
              className="w-full"
              data-testid="edit-achievement-recognition-period"
            />
          )}
        />

        {achievementKind === "code" && (
          <>
            <Controller
              name="sectionLegacyId"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  variant="searchableDropdown"
                  label="Recognized By"
                  data={sectionOptions}
                  query={sectionQuery}
                  loadingResults={sectionsLoading}
                  placeholder="Search sections"
                  data-testid="edit-award-recognized-by"
                  constrainMenuWidth
                  menuClassName="max-h-[300px]"
                  renderItem={(option) => <div className="text-body-large">{option.label}</div>}
                  onChangeText={(text) => {
                    setSectionQuery(text);
                    if (value) onChange("");
                  }}
                  reset={() => {
                    setSectionQuery("");
                    setDebouncedSectionQuery("");
                    onChange("");
                  }}
                  onSelect={(option) => {
                    onChange(option.value);
                    setSectionQuery(option.label);
                    setDebouncedSectionQuery("");
                  }}
                />
              )}
            />

            <Controller
              name="awardRemarks"
              control={control}
              render={({ field: { onChange, value } }) => (
                <TextField
                  type="textarea"
                  rows={3}
                  label="Remarks"
                  value={value}
                  onChangeText={onChange}
                  className="w-full"
                  data-testid="edit-award-remarks"
                />
              )}
            />
          </>
        )}
      </div>
    </Dialog>
  );
}
